import React from 'react';
import Icon from '../../../../components/AppIcon';

const MergePreview = ({ result, onBack, onConfirm }) => {
  const { companyA, companyB, mergedPreview } = result || {};

  return (
    <div className="space-y-6">
      {/* Merged Company Preview */}
      <div className="bg-gradient-to-br from-primary-50 to-secondary-50 rounded-xl shadow-sm border border-primary-200 p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
            <Icon name="Building2" size={24} color="white" />
          </div>
          <div>
            <p className="text-sm font-medium text-primary mb-1">Merged Company Preview</p>
            <h2 className="text-2xl font-bold text-text-primary">{mergedPreview?.name}</h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-text-secondary">Total Users</span>
              <Icon name="Users" size={20} color="#3B82F6" />
            </div>
            <p className="text-2xl font-bold text-text-primary">{mergedPreview?.totalUsers}</p>
            <p className="text-xs text-text-secondary mt-1">
              {companyA?.stats?.totalUsers || 0} + {companyB?.stats?.totalUsers || 0} (deduplicated)
            </p>
          </div>

          <div className="bg-white rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-text-secondary">Total Jobs</span>
              <Icon name="Briefcase" size={20} color="#10B981" />
            </div>
            <p className="text-2xl font-bold text-text-primary">{mergedPreview?.totalJobs}</p>
            <p className="text-xs text-text-secondary mt-1">
              {companyA?.stats?.totalJobs || 0} + {companyB?.stats?.totalJobs || 0}
            </p>
          </div>

          <div className="bg-white rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-text-secondary">Primary Owner</span>
              <Icon name="Shield" size={20} color="#F59E0B" />
            </div>
            <p className="text-sm font-semibold text-text-primary truncate">
              {companyA?.primary_owner?.full_name}
            </p>
            <p className="text-xs text-text-secondary mt-1 truncate">
              {companyA?.primary_owner?.email}
            </p>
          </div>
        </div>
      </div>

      {/* Before & After Comparison */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Company A */}
        <div className="bg-white rounded-xl shadow-sm border border-border p-6">
          <div className="flex items-center space-x-2 mb-4">
            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
              <span className="text-white font-bold">A</span>
            </div>
            <div>
              <p className="text-xs font-medium text-text-secondary">Primary Company</p>
              <h3 className="font-semibold text-text-primary">{companyA?.company_name}</h3>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between py-2 border-b border-border">
              <span className="text-sm text-text-secondary">Company Tag</span>
              <span className="text-sm font-medium text-text-primary">@{companyA?.company_tag}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-border">
              <span className="text-sm text-text-secondary">Total Users</span>
              <span className="text-sm font-medium text-text-primary">{companyA?.stats?.totalUsers || 0}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-border">
              <span className="text-sm text-text-secondary">Total Jobs</span>
              <span className="text-sm font-medium text-text-primary">{companyA?.stats?.totalJobs || 0}</span>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-text-secondary">Status</span>
              <span className="px-2 py-1 bg-success-50 text-success-700 text-xs font-medium rounded-full">
                {companyA?.status}
              </span>
            </div>
          </div>
        </div>

        {/* Company B */}
        <div className="bg-white rounded-xl shadow-sm border border-border p-6">
          <div className="flex items-center space-x-2 mb-4">
            <div className="w-10 h-10 bg-secondary-500 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold">B</span>
            </div>
            <div>
              <p className="text-xs font-medium text-text-secondary">Secondary Company</p>
              <h3 className="font-semibold text-text-primary">{companyB?.company_name}</h3>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between py-2 border-b border-border">
              <span className="text-sm text-text-secondary">Company Tag</span>
              <span className="text-sm font-medium text-text-primary">@{companyB?.company_tag}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-border">
              <span className="text-sm text-text-secondary">Total Users</span>
              <span className="text-sm font-medium text-text-primary">{companyB?.stats?.totalUsers || 0}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-border">
              <span className="text-sm text-text-secondary">Total Jobs</span>
              <span className="text-sm font-medium text-text-primary">{companyB?.stats?.totalJobs || 0}</span>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-text-secondary">Status</span>
              <span className="px-2 py-1 bg-success-50 text-success-700 text-xs font-medium rounded-full">
                {companyB?.status}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Merge Impact Summary */}
      <div className="bg-white rounded-xl shadow-sm border border-border p-6">
        <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center space-x-2">
          <Icon name="TrendingUp" size={20} color="#3B82F6" />
          <span>Merge Impact Summary</span>
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-text-primary mb-3">What Will Happen:</h4>
            <ul className="space-y-2">
              <li className="flex items-start space-x-2 text-sm text-text-secondary">
                <Icon name="Check" size={16} color="#10B981" className="mt-0.5 flex-shrink-0" />
                <span>All users from both companies will be consolidated</span>
              </li>
              <li className="flex items-start space-x-2 text-sm text-text-secondary">
                <Icon name="Check" size={16} color="#10B981" className="mt-0.5 flex-shrink-0" />
                <span>Jobs and projects will be merged into a single workspace</span>
              </li>
              <li className="flex items-start space-x-2 text-sm text-text-secondary">
                <Icon name="Check" size={16} color="#10B981" className="mt-0.5 flex-shrink-0" />
                <span>Company A will be the primary account with all permissions</span>
              </li>
              <li className="flex items-start space-x-2 text-sm text-text-secondary">
                <Icon name="Check" size={16} color="#10B981" className="mt-0.5 flex-shrink-0" />
                <span>Storage and files will be consolidated under one company</span>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-medium text-text-primary mb-3">Important Notes:</h4>
            <ul className="space-y-2">
              <li className="flex items-start space-x-2 text-sm text-warning-700">
                <Icon name="AlertTriangle" size={16} color="#F59E0B" className="mt-0.5 flex-shrink-0" />
                <span>Company B will be marked as archived after merge</span>
              </li>
              <li className="flex items-start space-x-2 text-sm text-warning-700">
                <Icon name="AlertTriangle" size={16} color="#F59E0B" className="mt-0.5 flex-shrink-0" />
                <span>This action cannot be undone in production</span>
              </li>
              <li className="flex items-start space-x-2 text-sm text-warning-700">
                <Icon name="AlertTriangle" size={16} color="#F59E0B" className="mt-0.5 flex-shrink-0" />
                <span>All company B subscriptions and billing will be transferred</span>
              </li>
              <li className="flex items-start space-x-2 text-sm text-warning-700">
                <Icon name="AlertTriangle" size={16} color="#F59E0B" className="mt-0.5 flex-shrink-0" />
                <span>Data migration may take several minutes to complete</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Simulation Notice */}
      <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 flex items-start space-x-3">
        <Icon name="Info" size={20} color="#3B82F6" className="flex-shrink-0 mt-0.5" />
        <div className="text-sm text-primary-900">
          <p className="font-medium mb-1">This is a Simulation Only</p>
          <p className="text-primary-800">
            No actual changes will be made to your companies. This preview is for planning purposes only. 
            Real merge operations require backend implementation and proper data migration procedures with rollback capabilities.
          </p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 border border-border text-text-primary rounded-lg font-medium hover:bg-secondary-50 transition-colors duration-200 flex items-center space-x-2"
        >
          <Icon name="ArrowLeft" size={20} />
          <span>Back to Conflicts</span>
        </button>
        
        <button
          onClick={onConfirm}
          className="px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-700 transition-colors duration-200 flex items-center space-x-2"
        >
          <Icon name="Zap" size={20} />
          <span>Confirm Merge (Simulation)</span>
        </button>
      </div>
    </div>
  );
};

export default MergePreview;